export type PrivacyPolicyBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'subheading'; text: string }
  | { kind: 'bullets'; items: readonly string[] }
  | { kind: 'table'; rows: readonly (readonly string[])[] };

export interface PrivacyPolicyDocument {
  heading: string;
  subtitle: string;
  effectiveVersion: string;
  blocks: readonly PrivacyPolicyBlock[];
}

interface PrivacyPolicyRelease {
  effectiveVersion: string;
  publishedAddress: string;
}

const buildPrivacyPolicyDocument = (
  release: PrivacyPolicyRelease
): PrivacyPolicyDocument => ({
  heading: 'Política de Privacidad y Tratamiento de Datos Personales',
  subtitle: 'Servicio SaaS multicliente provisto por CRYPTOLOGY S.A.C.',
  effectiveVersion: release.effectiveVersion,
  blocks: [
    {
      kind: 'table',
      rows: [
        [
          'Aviso principal Al iniciar sesión, crear una cuenta, acceder o hacer uso del Sistema, el Usuario declara haber sido informado sobre esta Política de Privacidad. Si el Usuario no está de acuerdo con el tratamiento necesario para operar el Sistema, no debe acceder ni utilizarlo.',
        ],
      ],
    },
    { kind: 'subheading', text: 'Identificación del proveedor' },
    {
      kind: 'table',
      rows: [
        ['Razón social', 'CRYPTOLOGY S.A.C.'],
        ['RUC', '20604970807'],
        ['Domicilio publicado', release.publishedAddress],
        ['Correo de contacto', 'hola@inmueble.red'],
        ['Sistema', 'Inmueble.red / CRM'],
      ],
    },
    { kind: 'subheading', text: 'Banco de Datos Personales inscrito' },
    {
      kind: 'table',
      rows: [
        [
          'Sistema registral',
          'SIPDP - Sistema Integrado de Protección de Datos Personales',
        ],
        ['Denominación', 'PROSPECTO DE CLIENTES'],
        ['Código del Banco de Datos', 'PN-2026-105'],
        ['Nro. Constancia', 'INS-2026-3488'],
        ['Fecha de registro', '11/06/2026'],
        ['Estado', 'INSCRITO'],
      ],
    },
    {
      kind: 'paragraph',
      text: 'Esta referencia corresponde al banco de datos personales usado para prospección comercial. Los datos operativos cargados por una Organización en el Sistema se rigen por la responsabilidad, finalidad, base legal e instrucciones de esa Organización conforme a esta Política, los contratos y los anexos aplicables.',
    },
    { kind: 'heading', text: '1. Alcance y roles de tratamiento' },
    {
      kind: 'paragraph',
      text: 'Esta Política informa cómo CRYPTOLOGY S.A.C. trata Datos Personales vinculados al acceso y uso del Sistema, incluyendo autenticación, administración de cuentas, seguridad, soporte, trazabilidad, comunicaciones operativas, mantenimiento e infraestructura del servicio SaaS multicliente.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando una Organización usa el Sistema para gestionar leads, prospectos, clientes, contactos, oportunidades, trabajadores, comunicaciones, documentos, tareas, reportes, operaciones inmobiliarias, pagos u otros registros de su propia actividad, dicha Organización define las finalidades y medios esenciales del tratamiento frente a los titulares de esos datos.',
    },
    {
      kind: 'paragraph',
      text: 'En esos casos, Cryptology actúa como proveedor tecnológico y, cuando corresponda, como encargado del tratamiento por cuenta de la Organización, procesando los datos conforme al contrato, a las instrucciones documentadas, a esta Política y a la normativa peruana aplicable.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology actúa como responsable respecto de los datos necesarios para administrar sus propias cuentas, accesos, seguridad, soporte, comunicaciones comerciales, registros contractuales, obligaciones legales y defensa de derechos.',
    },
    { kind: 'heading', text: '2. Datos personales tratados' },
    {
      kind: 'paragraph',
      text: 'El Sistema puede tratar las siguientes categorías de Datos Personales, según los módulos habilitados, el rol del Usuario y la información cargada por la Organización:',
    },
    {
      kind: 'bullets',
      items: [
        'Datos de cuenta y autenticación: nombre, apellidos, correo electrónico, empresa, rol, permisos, identificadores internos, estado de cuenta, metadatos de sesión, tokens y eventos de acceso.',
        'Datos operativos ingresados por la Organización: leads, prospectos, clientes, contactos, oportunidades, trabajadores, tareas, notas, comunicaciones, documentos, archivos, cotizaciones, reportes, operaciones inmobiliarias y registros comerciales o administrativos.',
        'Datos técnicos y de seguridad: dirección IP, navegador, agente de usuario, identificadores de solicitud, workspace, subdominio, eventos de auditoría, registros de actividad, intentos de inicio de sesión, errores, marcas de tiempo y señales de abuso o riesgo.',
        'Datos de soporte y comunicaciones: solicitudes de ayuda, mensajes, adjuntos, historial de atención, decisiones operativas comunicadas por la Organización y respuestas enviadas por Cryptology.',
        'Datos de integraciones habilitadas: identificadores, mensajes, estados, archivos o metadatos recibidos desde servicios externos conectados por la Organización para operar sus procesos dentro del Sistema.',
      ],
    },
    { kind: 'heading', text: '3. Datos sensibles' },
    {
      kind: 'paragraph',
      text: 'El Sistema no requiere datos sensibles para el uso ordinario del CRM. Los Usuarios no deben cargar datos de salud, biométricos, vida sexual, origen racial o étnico, opiniones políticas, convicciones religiosas o filosóficas, afiliación sindical, antecedentes personales o cualquier otro dato sensible salvo que la Organización haya verificado una base legal válida, una finalidad legítima, las medidas de seguridad reforzadas y los consentimientos exigibles.',
    },
    {
      kind: 'paragraph',
      text: 'Si una Organización carga datos sensibles bajo su propia decisión, la Organización mantiene responsabilidad por informar al titular, obtener el consentimiento cuando corresponda, limitar el acceso y cumplir las obligaciones aplicables como responsable del tratamiento.',
    },
    { kind: 'heading', text: '4. Finalidades del tratamiento' },
    {
      kind: 'paragraph',
      text: 'Cryptology trata Datos Personales únicamente para finalidades determinadas, explícitas y legítimas vinculadas al Sistema:',
    },
    {
      kind: 'bullets',
      items: [
        'crear, autenticar, administrar y proteger cuentas de Usuario;',
        'prestar, mantener, operar, monitorear y mejorar técnicamente el Sistema;',
        'aplicar roles, permisos, segregación por Organización y aislamiento lógico entre espacios de trabajo;',
        'registrar auditoría, trazabilidad, eventos de seguridad, disponibilidad, soporte y continuidad del servicio;',
        'atender solicitudes de soporte, diagnóstico, capacitación, mantenimiento, incidentes y comunicaciones operativas;',
        'procesar registros cargados por la Organización conforme a los módulos habilitados y a sus instrucciones;',
        'habilitar integraciones, automatizaciones, colas, importaciones, exportaciones, notificaciones y reportes solicitados por la Organización;',
        'prevenir fraude, abuso, accesos no autorizados, ataques, errores graves, pérdida de información e incumplimientos de seguridad;',
        'cumplir obligaciones legales, requerimientos de autoridades competentes y defensa de derechos de Cryptology, la Organización, Usuarios o terceros.',
      ],
    },
    { kind: 'heading', text: '5. Base legal, consentimiento e instrucciones' },
    {
      kind: 'paragraph',
      text: 'El tratamiento de datos de cuenta, acceso, seguridad, soporte y operación se realiza porque es necesario para prestar el servicio solicitado, ejecutar la relación contractual o precontractual, cumplir obligaciones legales, atender intereses legítimos de seguridad y continuidad, o contar con consentimiento cuando la ley lo exige.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando el Sistema procese Datos Personales ingresados por la Organización, la Organización debe asegurar que cuenta con la base legal, consentimiento, aviso informativo, legitimación, exactitud, finalidad, conservación y mecanismos de atención de derechos que correspondan conforme a la Ley N.° 29733, su Reglamento y normas complementarias.',
    },
    {
      kind: 'paragraph',
      text: 'Los datos exigidos para iniciar sesión, validar identidad, asignar rol, aplicar permisos, auditar accesos o proteger la plataforma son necesarios para operar el Sistema. Si esos datos no se proporcionan o son eliminados cuando siguen siendo requeridos, Cryptology puede no estar en condiciones de otorgar acceso o mantener la cuenta activa.',
    },
    { kind: 'heading', text: '6. Destinatarios y encargados' },
    {
      kind: 'paragraph',
      text: 'Los Datos Personales pueden ser tratados por personal autorizado de Cryptology bajo deberes de confidencialidad, por Usuarios autorizados de la Organización según sus roles y permisos, y por proveedores tecnológicos que prestan servicios de infraestructura, alojamiento, almacenamiento, correo, seguridad, monitoreo, soporte, autenticación, respaldo, inteligencia artificial, analítica operativa o integraciones necesarias para el Sistema.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology no vende Datos Personales ni los comunica a terceros para finalidades ajenas al Sistema. La comunicación a autoridades públicas, judiciales, administrativas o regulatorias se realizará únicamente cuando exista obligación legal, mandato competente o necesidad de defensa de derechos.',
    },
    { kind: 'heading', text: '7. Flujo transfronterizo' },
    {
      kind: 'paragraph',
      text: 'El Sistema opera sobre infraestructura cloud y proveedores tecnológicos que pueden procesar o almacenar información dentro o fuera del Perú. Cuando exista flujo transfronterizo de Datos Personales, Cryptology lo limita a las finalidades declaradas y aplica medidas contractuales, técnicas y organizativas razonables conforme a la normativa peruana aplicable.',
    },
    { kind: 'heading', text: '8. Conservación' },
    {
      kind: 'paragraph',
      text: 'Los Datos Personales se conservan mientras sean necesarios para prestar el Sistema, mantener la relación contractual, cumplir instrucciones de la Organización, operar auditoría y seguridad, atender soporte, resolver reclamos, cumplir obligaciones legales o defender derechos.',
    },
    {
      kind: 'paragraph',
      text: 'Los Datos de la Organización se conservan conforme al contrato, configuración, instrucciones documentadas y ciclos operativos aplicables. Los respaldos, registros técnicos, logs de seguridad y eventos de auditoría pueden conservarse por periodos adicionales razonables cuando sean necesarios para continuidad, trazabilidad, seguridad, cumplimiento o defensa de derechos.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando los datos dejan de ser necesarios o una instrucción válida exige su eliminación, se eliminan, bloquean, anonimizan o segregan según corresponda y conforme a las limitaciones técnicas, legales y contractuales aplicables.',
    },
    { kind: 'heading', text: '9. Seguridad' },
    {
      kind: 'paragraph',
      text: 'Cryptology aplica medidas técnicas, organizativas y contractuales razonables para proteger Datos Personales contra acceso no autorizado, pérdida, alteración, divulgación, uso indebido o destrucción no autorizada, de acuerdo con la naturaleza del Sistema y el riesgo del tratamiento.',
    },
    {
      kind: 'bullets',
      items: [
        'controles de acceso por rol, empresa y permisos;',
        'segregación lógica entre Organizaciones dentro de la modalidad SaaS multicliente;',
        'autenticación, sesiones, tokens, rotación y controles de bloqueo cuando corresponda;',
        'seguridad de transporte, validación de entradas, rate limits y monitoreo de abuso;',
        'auditoría, trazabilidad, logs técnicos, respaldos y procedimientos de continuidad;',
        'limitación de acceso por personal autorizado y proveedores necesarios para operar el Sistema;',
        'gestión de incidentes, diagnóstico y comunicación cuando la normativa aplicable lo exija.',
      ],
    },
    {
      kind: 'paragraph',
      text: 'La seguridad también depende de la Organización y del Usuario. El Usuario debe proteger sus credenciales, no compartir contraseñas, cerrar sesiones en dispositivos compartidos, usar autenticación multifactor cuando esté disponible y reportar accesos sospechosos o errores graves.',
    },
    { kind: 'heading', text: '10. Derechos de los titulares' },
    {
      kind: 'paragraph',
      text: 'Los titulares pueden ejercer sus derechos de información, acceso, actualización, inclusión, rectificación, supresión o cancelación, oposición y revocación del consentimiento cuando corresponda. Para datos cargados por una Organización en su propio espacio de trabajo, el titular debe dirigir primero su solicitud a esa Organización, que actúa como responsable frente a sus datos operativos.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando una solicitud corresponda a datos bajo responsabilidad directa de Cryptology o requiera asistencia técnica sobre datos procesados por cuenta de una Organización, puede enviarse al correo hola@inmueble.red indicando identidad del solicitante, derecho que desea ejercer, datos vinculados, medio de respuesta y sustento aplicable.',
    },
    {
      kind: 'paragraph',
      text: 'El derecho de acceso se responde dentro de 20 días hábiles. Los derechos de rectificación, cancelación u oposición se responden dentro de 10 días hábiles. Si el titular no recibe respuesta en el plazo aplicable o su solicitud es denegada, puede presentar reclamo ante la Autoridad Nacional de Protección de Datos Personales.',
    },
    { kind: 'heading', text: '11. Menores de edad y datos de terceros' },
    {
      kind: 'paragraph',
      text: 'El Sistema está dirigido a Usuarios autorizados por Organizaciones y no está destinado a menores de edad como usuarios directos. Si una Organización trata datos de menores o de terceros dentro del Sistema, la Organización debe contar con la autorización, base legal, finalidad, información previa y medidas reforzadas que correspondan.',
    },
    { kind: 'heading', text: '12. Cookies, almacenamiento local y monitoreo' },
    {
      kind: 'paragraph',
      text: 'El frontend del Sistema puede usar almacenamiento local o de sesión, tokens, preferencias, parámetros de navegación, identificadores técnicos y herramientas de monitoreo de errores cuando estén configuradas para mantener la sesión, recordar idioma o preferencias operativas, proteger accesos, diagnosticar fallas y sostener la continuidad del servicio.',
    },
    {
      kind: 'paragraph',
      text: 'Estos mecanismos no deben utilizarse para evadir permisos, compartir sesiones o acceder a información de otra Organización. El bloqueo de almacenamiento técnico esencial puede impedir el inicio de sesión o el funcionamiento correcto del Sistema.',
    },
    { kind: 'heading', text: '13. Actualizaciones y contacto' },
    {
      kind: 'paragraph',
      text: 'Cryptology puede actualizar esta Política para reflejar cambios legales, operativos, técnicos, contractuales o de seguridad. La versión vigente se publicará en esta página y, cuando corresponda, se informarán cambios relevantes mediante el Sistema o los canales contractuales disponibles.',
    },
    {
      kind: 'paragraph',
      text: 'Para consultas, solicitudes o comunicaciones relacionadas con Datos Personales, escribe a hola@inmueble.red.',
    },
  ],
});

// Release 1.1 stays published as history. The legal manifest pins it by
// checksum, so its text and the domicile published at the time never change.
export const privacyPolicyDocumentV1_1 = buildPrivacyPolicyDocument({
  effectiveVersion: 'Versión 1.1 | Vigente desde el 11 de junio de 2026',
  publishedAddress: 'Malecón Cisneros 428, Miraflores, 15074, Lima, Perú',
});

export const privacyPolicyDocument = buildPrivacyPolicyDocument({
  effectiveVersion: 'Versión 1.2 | Vigente desde el 7 de octubre de 2026',
  publishedAddress:
    'Calle Conde de la Monclova 378, 15073, San Isidro, Lima, Perú',
});
